/* Clothxtra cart drawer + cart page.
   - Every "Add to cart / Add to bag" form on the site is caught here, added with AJAX and the drawer opens.
   - <cart-drawer> keeps Dawn's interface (renderContents, getSectionsToRender) for the quick view and + buttons.
   - Works even if Theme settings → Cart type is still Notification or Drawer. */
(() => {
  const BUBBLE = 'cart-icon-bubble';

  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  function replaceInner(sectionId, html, selector) {
    if (!html) return;
    const fresh = parse(html).querySelector(selector);
    if (!fresh) return;
    const target = document.querySelector(selector);
    if (target) target.innerHTML = fresh.innerHTML;
  }

  function updateBubble(html) {
    const bubble = document.getElementById(BUBBLE);
    if (!bubble || !html) return;
    const fresh = parse(html).querySelector('.shopify-section');
    if (fresh) bubble.innerHTML = fresh.innerHTML;
  }

  function cartSections() {
    // every Clothxtra cart area on the page (drawer, cart page)
    const map = new Map();
    document.querySelectorAll('cx-cart-items[data-section-id]').forEach((el) => {
      map.set(el.dataset.sectionId, el.dataset.inner);
    });
    return map;
  }

  /* ---------------- Drawer ----------------
     Everything works from the .cx-cd element itself, so it keeps working even when Dawn's own
     cart (notification or drawer) is still switched on in Theme settings → Cart. */
  const nativeFetch = window.fetch.bind(window);
  const getEl = () => document.querySelector('.cx-cd');
  const onCartPage = () => getEl()?.dataset.onCartPage === 'true';
  let lastFocus = null;
  let closeTimer = null;

  const Drawer = {
    el: getEl,

    sections() {
      // the drawer, the cart page (when on /cart) and the header cart icon
      const el = getEl();
      const list = [];
      cartSections().forEach((selector, id) => list.push({ id, selector }));
      if (el && !list.some((x) => x.id === el.dataset.sectionId)) list.push({ id: el.dataset.sectionId, selector: '.cx-cd__inner' });
      list.push({ id: BUBBLE });
      return list;
    },

    render(sections) {
      if (!sections) return;
      this.sections().forEach(({ id, selector }) => {
        if (selector && sections[id]) replaceInner(id, sections[id], selector);
      });
      updateBubble(sections[BUBBLE]);
      this.syncEmpty();
    },

    async refresh() {
      const ids = this.sections().map((x) => x.id);
      try {
        const res = await nativeFetch(`${window.location.pathname}?sections=${ids.join(',')}`, { headers: { Accept: 'application/json' } });
        this.render(await res.json());
      } catch (e) {
        console.error('cx-cart refresh', e);
      }
    },

    syncEmpty() {
      const el = getEl();
      if (el) el.classList.toggle('is-empty', !el.querySelector('.cx-line'));
    },

    open(trigger) {
      const el = getEl();
      if (!el || onCartPage()) return;
      if (trigger) lastFocus = trigger;
      clearTimeout(closeTimer);
      this.syncEmpty();
      if (el.classList.contains('is-open')) return;
      el.hidden = false;
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          el.classList.add('is-open');
          const panel = el.querySelector('.cx-cd__panel');
          if (typeof trapFocus === 'function') trapFocus(panel, el.querySelector('[data-cx-cd-close]') || panel);
          else panel?.focus();
        })
      );
      document.body.classList.add('overflow-hidden');
    },

    close() {
      const el = getEl();
      if (!el || !el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(lastFocus);
      else lastFocus?.focus?.();
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      closeTimer = setTimeout(() => (el.hidden = true), reduce ? 0 : 320);
    },
  };
  window.CxCartDrawer = Drawer;

  // Dawn-compatible <cart-drawer>: anything that calls renderContents / getSectionsToRender
  // (Dawn's product form, the Clothxtra quick view and + buttons) is sent to this drawer.
  if (!customElements.get('cart-drawer')) {
    customElements.define(
      'cart-drawer',
      class CxCartDrawerElement extends HTMLElement {
        getSectionsToRender() {
          return Drawer.sections();
        }
        setActiveElement(el) {
          lastFocus = el;
        }
        renderContents(parsed) {
          if (!parsed || !parsed.sections) {
            window.location.href = getEl()?.dataset.cartUrl || '/cart';
            return;
          }
          Drawer.render(parsed.sections);
          setTimeout(() => Drawer.open());
        }
        syncEmpty() {
          Drawer.syncEmpty();
        }
        open(trigger) {
          Drawer.open(trigger);
        }
        close() {
          Drawer.close();
        }
      }
    );
  }

  // close: X button, overlay, Esc
  document.addEventListener('click', (e) => {
    if (e.target.closest('.cx-cd [data-cx-cd-close]')) {
      e.preventDefault();
      Drawer.close();
    }
  });
  document.addEventListener('keyup', (e) => {
    if (e.key === 'Escape') Drawer.close();
  });

  // header cart icon opens the drawer (except on the cart page itself)
  document.addEventListener(
    'click',
    (e) => {
      const icon = e.target.closest(`#${BUBBLE}`);
      if (!icon || !getEl() || onCartPage()) return;
      e.preventDefault();
      e.stopPropagation();
      Drawer.open(icon);
    },
    true
  );

  /* ---------------- Add to cart from ANY form ----------------
     Catches every <form action="/cart/add"> on the site (product page "Add to bag",
     Dawn product forms, quick add) before Dawn sees it, adds the item and opens the drawer. */
  function formError(form, text) {
    const host = form.closest('product-form') || form.parentElement || form;
    const wrap = host.querySelector('.product-form__error-message-wrapper');
    if (wrap) {
      const msg = wrap.querySelector('.product-form__error-message') || wrap;
      msg.textContent = text || '';
      wrap.hidden = !text;
      wrap.toggleAttribute('hidden', !text);
      return;
    }
    let note = form.querySelector('.cx-add-error');
    if (!text) return note?.remove();
    if (!note) {
      note = document.createElement('p');
      note.className = 'cx-add-error';
      note.setAttribute('role', 'alert');
      form.appendChild(note);
    }
    note.textContent = text;
  }

  async function addFromForm(form, button) {
    const spinner = (form.closest('product-form') || form).querySelector('.loading__spinner');
    button?.setAttribute('aria-disabled', 'true');
    button?.classList.add('loading', 'is-loading');
    spinner?.classList.remove('hidden');
    formError(form, '');

    const body = new FormData(form);
    if (button?.name && button.value && !body.has(button.name)) body.append(button.name, button.value);
    body.append('sections', Drawer.sections().map((x) => x.id).join(','));
    body.append('sections_url', window.location.pathname);

    try {
      const res = await nativeFetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await res.json();
      if (data.status) {
        formError(form, data.description || data.message || window.cartStrings?.error || 'Error');
        return;
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cx-cart', productVariantId: body.get('id'), cartData: data });
      }
      form.closest('quick-add-modal')?.hide?.(true);
      Drawer.render(data.sections);
      setTimeout(() => Drawer.open(button), form.closest('quick-add-modal') ? 250 : 0);
    } catch (err) {
      console.error('cx-cart add', err);
      formError(form, window.cartStrings?.error || 'Could not add to cart');
    } finally {
      button?.classList.remove('loading', 'is-loading');
      button?.removeAttribute('aria-disabled');
      spinner?.classList.add('hidden');
    }
  }

  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement) || !getEl()) return;
      if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
      if (form.hasAttribute('data-cx-no-drawer')) return;
      const button = e.submitter || form.querySelector('[type="submit"]');
      e.preventDefault();
      e.stopImmediatePropagation(); // Dawn's product form never runs, so its notification/redirect can't take over
      if (button && (button.disabled || button.getAttribute('aria-disabled') === 'true' || button.classList.contains('is-loading'))) return;
      addFromForm(form, button);
    },
    true
  );

  /* ---------------- Safety net for other add-to-cart code ----------------
     If any other script (an app, a custom button) adds to the cart with fetch and doesn't
     refresh this drawer itself, refresh it and open it. */
  function handledByUs(init) {
    const h = init?.headers;
    if (h && (h instanceof Headers ? h.get('X-Cx-Cart') : h['X-Cx-Cart'])) return true;
    const id = getEl()?.dataset.sectionId;
    const b = init?.body;
    if (!id || !b) return false;
    if (b instanceof FormData || b instanceof URLSearchParams) return String(b.get('sections') || '').includes(id);
    if (typeof b === 'string') return b.includes(id);
    return false;
  }

  let addTimer;
  window.fetch = function (input, init) {
    const request = nativeFetch(input, init);
    try {
      const url = typeof input === 'string' ? input : input?.url || String(input || '');
      const method = String(init?.method || input?.method || 'GET').toUpperCase();
      const path = new URL(url, window.location.href).pathname;
      if (method === 'POST' && /\/cart\/add(\.js|\.json)?$/.test(path) && getEl() && !handledByUs(init)) {
        request
          .then((res) => {
            if (!res.ok) return;
            clearTimeout(addTimer);
            addTimer = setTimeout(async () => {
              await Drawer.refresh();
              Drawer.open();
            }, 120);
          })
          .catch(() => {});
      }
    } catch (e) {
      /* never break the original request */
    }
    return request;
  };

  /* ---------------- Quantity / delete (drawer + cart page) ---------------- */
  if (!customElements.get('cx-cart-items')) {
    class CxCartItems extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-cx-line-qty]');
          if (!btn) return;
          e.preventDefault();
          const line = btn.closest('.cx-line');
          this.update(line, Math.max(0, parseInt(btn.dataset.cxLineQty, 10) || 0));
        });

        const note = this.querySelector('[data-cx-cart-note]');
        if (note) {
          let t;
          note.addEventListener('input', () => {
            clearTimeout(t);
            t = setTimeout(() => {
              fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ note: note.value }),
              });
            }, 400);
          });
        }

        this.loadRecos();
      }

      message(text) {
        const msg = this.querySelector('[data-cx-cart-msg]');
        if (!msg) return;
        msg.textContent = text || '';
        msg.hidden = !text;
      }

      async update(lineEl, quantity) {
        if (!lineEl || this.busy) return;
        this.busy = true;
        lineEl.classList.add('is-loading');
        this.message('');

        const sections = cartSections();
        const ids = [...sections.keys(), BUBBLE];
        const key = lineEl.dataset.key;

        try {
          const res = await fetch(`${window.routes?.cart_change_url || '/cart/change'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: Number(lineEl.dataset.line), quantity, sections: ids, sections_url: window.location.pathname }),
          });
          const data = await res.json();
          if (data.errors || data.status) {
            this.message(data.errors || data.description || data.message || window.cartStrings?.error || 'Error');
            lineEl.classList.remove('is-loading');
            return;
          }

          sections.forEach((selector, id) => replaceInner(id, data.sections?.[id], selector));
          updateBubble(data.sections?.[BUBBLE]);
          Drawer.syncEmpty();

          // asked for more than is in stock
          const item = (data.items || []).find((i) => i.key === key);
          if (quantity > 0 && item && item.quantity < quantity) {
            const text = (window.cartStrings?.quantityError || 'You can only add [quantity] of this item to your cart.').replace('[quantity]', item.quantity);
            document.querySelectorAll('[data-cx-cart-msg]').forEach((m) => {
              m.textContent = text;
              m.hidden = false;
            });
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cx-cart', cartData: data });
          }
        } catch (err) {
          console.error('cx-cart', err);
          this.message(window.cartStrings?.error || 'Error');
          lineEl.classList.remove('is-loading');
        } finally {
          this.busy = false;
        }
      }

      async loadRecos() {
        const box = this.querySelector('[data-cx-recos]');
        if (!box || !box.dataset.url) return;
        try {
          const res = await fetch(box.dataset.url);
          const list = parse(await res.text()).querySelector('[data-cx-recos-result]');
          if (!list || !list.children.length) return;
          box.querySelector('[data-cx-recos-target]').replaceChildren(list);
          box.hidden = false;
        } catch (e) {
          /* recommendations are optional */
        }
      }
    }
    customElements.define('cx-cart-items', CxCartItems);
  }
})();